import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppStateFacade } from '@app-state';
import {
    PageMetadata,
    PageMetadataRegistry,
} from '@app/router';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import {
    combineLatest,
    Observable,
    Subject,
} from 'rxjs';
import {
    map,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { HeaderOverrideService } from './header-override.service';

@Component({
    selector: 'ui-top-navigation',
    templateUrl: './ui-top-navigation.component.html',
    styleUrls: [
        './ui-top-navigation.component.scss',
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiTopNavigationComponent implements OnDestroy {
    @ViewChild('pageAriaTitle', { static: true })
    public pageAriaTitle!: ElementRef;

    public items$: Observable<PageMetadata[]>;

    public activePage$ = this._pageRegistry.activePage$;

    private _destroyed$ = new Subject();

    public get currentLink() {
        return this._pageRegistry.cleanCurrentUrl;
    }

    constructor(
        public headerOverride: HeaderOverrideService,
        private _pageRegistry: PageMetadataRegistry,
        private _appState: AppStateFacade,
        route: ActivatedRoute,
    ) {
        const page$ = this._pageRegistry
            .activePage$
            .pipe(
                tap(() => this.focusPageAriaTitle()),
            );

        this.items$ = combineLatest([
            page$,
            route.queryParamMap,
        ]).pipe(
            map(([page, queryParamMap]) => _pageRegistry.find(queryParamMap.get('target')!) || page),
            filterNullOrUndefined(),
            map(this._determineHeaderItems),
            takeUntil(this._destroyed$),
        );
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public isActive = (page: PageMetadata) =>
        this._pageRegistry.isActivePage(page)

    public focusPageAriaTitle() {
        if (this.pageAriaTitle) {
            this.pageAriaTitle.nativeElement.focus();
        }
    }

    private _determineHeaderItems = (page: PageMetadata) => {
        const searchTitle = page.hasParent
            ? page.parent!.title
            : page.title;

        const children = page.type === 'Breadcrumb'
            ? [page]
            : this._pageRegistry.pages
                .filter(p =>
                    (!p.featureFlag || this._appState.settings.isFeatureEnabled(p.featureFlag)) &&
                    p.type === 'SubMenu' &&
                    p.parent &&
                    p.parent.title === searchTitle,
                );

        const main = page.parent || page;

        return [main, ...children];
    }
}
